import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
/**
 * 这段代码是一个用于计算页面的 First Meaningful Paint（FMP） 的 JavaScript 类。FMP 是指页面上最重要的部分（例如主要内容、导航栏等）第一次被用户视觉感知的时间点。

这个类包含了以下几个属性和方法：

statusCollector：一个数组，用于收集页面状态。
flag：一个布尔值，表示是否允许继续计算 FMP。
observer：一个 MutationObserver 对象，用于监听页面变化。
callbackCount：一个数字，用于记录回调函数的次数。
entries：一个对象，保存了页面状态的时间戳。
fmpCallback：一个回调函数，用于在计算完成 FMP 后执行。
接下来是这个类中定义的一些接口：

Entry：一个接口，包含了页面元素的名称和响应结束的时间戳。
ElementWeight：一个接口，包含了元素名称和它们的权重。
FmpEl：一个接口，包含了一个 DOM 元素、它的时间戳和它的权重。
FmpCalScore：一个接口，包含了一个 DOM 元素、它的子元素、它们的时间戳和它们的权重。
FmpResult：一个接口，包含了计算 FMP 后返回的信息。
接下来是这个类中定义的一些静态属性：

IGNORE_TAG_SET：一个数组，包含了一些不需要计算 FMP 的元素。
ELE_WEIGHT：一个对象，包含了元素名称和它们的权重。
LIMIT：一个数字，表示计算 FMP 的最长时间。
WW：一个数字，表示窗口的宽度。
WH：一个数字，表示窗口的高度。
DELAY：一个数字，表示计算 FMP 的延迟时间。
最后是这个类中定义的一些方法：

getFirstSnapShot()：获取页面的第一个快照。
initOberver()：初始化 MutationObserver 对象，并开始监听页面变化。
calculateFinalScore()：计算 FMP 并执行回调函数。
checkNeedCancel(startTime: number)：检查是否需要取消计算 FMP。
setTag($parent: Element, callbackCount: number)：给页面元素添加 FMP 标记。
calculateFmpScore(allElement: HTMLCollectionOf<Element>)：计算 FMP 并返回结果。
calculateScore(root: FmpCalScore, fmpElArr: FmpEl[], maxWeight: number, resultEls: FmpEl[], tbt: number)：计算 FMP 分数。
 */

export var FMPTiming = /*#__PURE__*/function () {
  function FMPTiming(fmpCallback) {
    _classCallCheck(this, FMPTiming);
    _defineProperty(this, "statusCollector", []);
    _defineProperty(this, "flag", true);
    _defineProperty(this, "observer", null);
    _defineProperty(this, "callbackCount", 0);
    _defineProperty(this, "entries", {});
    _defineProperty(this, "fmpCallback", null);
    if (fmpCallback) {
      this.fmpCallback = fmpCallback;
    }
    this.initOberver();
  }
  _createClass(FMPTiming, [{
    key: "getFirstSnapShot",
    value: function getFirstSnapShot() {
      var time = performance.now();
      var $body = document.body;
      if ($body) {
        this.setTag($body, this.callbackCount);
      }
      this.statusCollector.push({
        time: time
      });
    }
  }, {
    key: "initOberver",
    value: function initOberver() {
      var _this = this;
      this.getFirstSnapShot();
      this.observer = new MutationObserver(function () {
        _this.callbackCount += 1;
        var time = performance.now();
        var $body = document.body;
        if ($body) {
          _this.setTag($body, _this.callbackCount);
        }
        _this.statusCollector.push({
          time: time
        });
      });
      this.observer.observe(document, {
        childList: true,
        subtree: true
      });
      if (document.readyState === 'complete') {
        this.calculateFinalScore();
      } else {
        window.addEventListener('load', function () {
          _this.calculateFinalScore();
        });
      }
    }
  }, {
    key: "calculateFinalScore",
    value: function calculateFinalScore() {
      if (MutationEvent && this.flag) {
        if (this.checkNeedCancel(performance.now())) {
          var _this$observer;
          (_this$observer = this.observer) === null || _this$observer === void 0 ? void 0 : _this$observer.disconnect();
          this.flag = false;
          if (this.fmpCallback) {
            this.fmpCallback({
              tp: null,
              resultEls: [],
              fmpTiming: -1
            });
          }
          return;
        }
      }
      this.entries = this.statusCollector.reduce(function (prev, current, index) {
        prev[index] = current.time;
        return prev;
      }, {});
      var allElement = document.getElementsByTagName('*');
      var fmpResult = this.calculateFmpScore(allElement);
      if (this.fmpCallback) {
        this.fmpCallback(fmpResult);
      }
    }
  }, {
    key: "checkNeedCancel",
    value: function checkNeedCancel(startTime) {
      return performance.now() - startTime > FMPTiming.LIMIT;
    }
  }, {
    key: "setTag",
    value: function setTag($parent, callbackCount) {
      var children = $parent.children;
      if (!children) {
        return;
      }
      for (var i = 0; i < children.length; i++) {
        var node = children[i];
        var tag = node.tagName;
        if (FMPTiming.IGNORE_TAG_SET.indexOf(tag) !== -1) {
          continue;
        }
        var num = callbackCount + i / 10;
        node.setAttribute('fmp', num.toString());
        this.setTag(node, num);
      }
    }
  }, {
    key: "calculateFmpScore",
    value: function calculateFmpScore(allElement) {
      var fmpElArr = [];
      var maxWeight = 0;
      var resultEls = [];
      var tbt = 0;
      var tp = this.calculateScore({
        els: [],
        st: 0,
        dpss: [],
        root: document.documentElement
      }, fmpElArr, maxWeight, resultEls, tbt);
      resultEls = resultEls.sort(function (a, b) {
        return b.weight - a.weight;
      }).slice(0, 5);
      var fmpTiming = resultEls.reduce(function (prev, current) {
        return Math.max(prev, current.st);
      }, 0);
      return {
        tp: tp,
        resultEls: resultEls,
        fmpTiming: fmpTiming
      };
    }
  }, {
    key: "calculateScore",
    value: function calculateScore(root, fmpElArr, maxWeight, resultEls, tbt) {
      var dpss = [];
      var allChildren = root.root.children;
      var _maxWeight = maxWeight;
      var _tbt = tbt;
      for (var i = 0; i < allChildren.length; i++) {
        var child = allChildren[i];
        var fmpNum = parseFloat(child.getAttribute('fmp') || '-1');
        if (fmpNum === -1) {
          continue;
        }
        var st = this.entries[Math.floor(fmpNum)];
        if (!st) {
          continue;
        }
        var elWeight = FMPTiming.ELE_WEIGHT[child.tagName] || 1;
        _maxWeight = Math.max(elWeight, maxWeight);
        var newFmpEl = {
          $node: child,
          st: st,
          weight: elWeight
        };
        fmpElArr.push(newFmpEl);
      }
      if (fmpElArr.length > 0) {
        fmpElArr.forEach(function (el) {
          return el.weight /= _maxWeight;
        });
        fmpElArr.forEach(function (el) {
          return _tbt += (el.st - root.st) * el.weight;
        });
      }
      if (allChildren.length > 0) {
        for (var _i = 0; _i < allChildren.length; _i++) {
          var _child = allChildren[_i];
          var _fmpNum = parseFloat(_child.getAttribute('fmp') || '-1');
          if (_fmpNum === -1) {
            continue;
          }
          var _st = this.entries[Math.floor(_fmpNum)];
          if (!_st) {
            continue;
          }
          var _elWeight = FMPTiming.ELE_WEIGHT[_child.tagName] || 1;
          var _newFmpEl = {
            $node: _child,
            st: _st,
            weight: _elWeight
          };
          var fmpCalScore = {
            els: [].concat(_toConsumableArray(root.els), [_newFmpEl]),
            st: _st,
            dpss: [],
            root: _child
          };
          var result = this.calculateScore(fmpCalScore, fmpElArr, _maxWeight, resultEls, _tbt);
          if (result) {
            dpss.push(result);
          }
        }
      }
      var currentScore = {
        els: root.els,
        st: root.st,
        dpss: dpss,
        root: root.root
      };
      if (dpss.length === 0) {
        return currentScore;
      }
      dpss.sort(function (a, b) {
        return b.st - a.st;
      });
      currentScore.els = dpss[0].els;
      currentScore.st = dpss[0].st;
      currentScore.dpss = dpss;
      return currentScore;
    }
  }]);
  return FMPTiming;
}();
_defineProperty(FMPTiming, "IGNORE_TAG_SET", ['SCRIPT', 'STYLE', 'META', 'HEAD', 'LINK']);
_defineProperty(FMPTiming, "ELE_WEIGHT", {
  SVG: 2,
  IMG: 2,
  CANVAS: 4,
  OBJECT: 4,
  EMBED: 4,
  VIDEO: 4
});
_defineProperty(FMPTiming, "LIMIT", 3000);
_defineProperty(FMPTiming, "WW", window.innerWidth);
_defineProperty(FMPTiming, "WH", window.innerHeight);
_defineProperty(FMPTiming, "DELAY", 500);